import type { SubscriptionAnalyticsOverview } from '../types/api/dashboard';

function toNum(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function parsePaymentMap(v: unknown): SubscriptionAnalyticsOverview['paymentStatusDistribution'] {
  if (!v || typeof v !== 'object') return {};
  const out: SubscriptionAnalyticsOverview['paymentStatusDistribution'] = {};
  for (const [key, val] of Object.entries(v as Record<string, unknown>)) {
    if (key === 'PENDING' || key === 'COMPLETED' || key === 'FAILED') {
      out[key] = toNum(val);
    }
  }
  return out;
}

function emptyOverview(): SubscriptionAnalyticsOverview {
  return {
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    cancelledSubscriptions: 0,
    newSubscriptions: 0,
    upcomingExpirations: 0,
    paymentStatusDistribution: {},
  };
}

/**
 * Normalizes GET /admin/analytics/subscriptions/overview `data` payloads.
 * Supports camelCase (Postman), snake_case, and the older inferred dashboard shape.
 */
export function parseSubscriptionAnalyticsOverview(raw: unknown): SubscriptionAnalyticsOverview {
  if (!raw || typeof raw !== 'object') return emptyOverview();
  const r = raw as Record<string, unknown>;

  if ('activeSubscriptions' in r) {
    return {
      activeSubscriptions: toNum(r.activeSubscriptions),
      expiredSubscriptions: toNum(r.expiredSubscriptions),
      cancelledSubscriptions: toNum(r.cancelledSubscriptions),
      newSubscriptions: toNum(r.newSubscriptions),
      upcomingExpirations: toNum(r.upcomingExpirations),
      paymentStatusDistribution: parsePaymentMap(r.paymentStatusDistribution),
    };
  }

  if ('active_subscriptions' in r) {
    return {
      activeSubscriptions: toNum(r.active_subscriptions),
      expiredSubscriptions: toNum(r.expired_subscriptions),
      cancelledSubscriptions: toNum(r.cancelled_subscriptions),
      newSubscriptions: toNum(r.new_subscriptions),
      upcomingExpirations: toNum(r.upcoming_expirations),
      paymentStatusDistribution: parsePaymentMap(r.payment_status_distribution),
    };
  }

  // Legacy inferred contract (older mocks / transitional backends)
  if ('totalActiveSubscribers' in r) {
    return {
      activeSubscriptions: toNum(r.totalActiveSubscribers),
      expiredSubscriptions: 0,
      cancelledSubscriptions: toNum(r.cancellations),
      newSubscriptions: toNum(r.newSubscriptions),
      upcomingExpirations: 0,
      paymentStatusDistribution: {
        FAILED: toNum(r.failedPayments),
      },
    };
  }

  return emptyOverview();
}
